import RootLayout from '@components/Pages/RootLayout';
import { RoleProtectedPage } from '@hclinear/hc-linear-react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
} from '@tanstack/react-router';
import React, { lazy } from 'react';
import { validateAnnouncementSearch } from '@/page/announcement/data/announcementPage.data';
import { validateStopSearch } from '@/page/stop/data/stopPage.data';
import { PermissionType } from '@/type';
import { APP_ROUTES } from '@/util';

const DynamicTimetablesPage = lazy(
  () => import('@/page/dynamic-timetable/DynamicTimetablesPage'),
);

const DynamicTimeTableEditor = lazy(
  () => import('@components/dynamic-timetable/editor/DynamicTimeTableEditor'),
);

const ErrorPage = lazy(() => import('@/page/error/ErrorPage'));

const SignInPage = lazy(() => import('@/page/auth/SignInPage'));
const ForgotPasswordPage = lazy(() => import('@/page/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/page/auth/ResetPasswordPage'));

const DashboardPage = lazy(() => import('@/page/dashboard/DashboardPage'));
const FlagPage = lazy(() => import('@/page/flag/FlagPage'));
const StateManagementPage = lazy(
  () => import('@/page/state-management/StateManagementPage'),
);
const ImagePage = lazy(() => import('@/page/image/ImagePage'));
const LayoutPage = lazy(() => import('@/page/layout/LayoutPage'));
const LayoutEditorPage = lazy(() => import('@/page/layout/LayoutEditorPage'));
const FontPage = lazy(() => import('@/page/font/FontPage'));
const ProfilePage = lazy(() => import('@/page/user/ProfilePage'));

const DisplayPage = lazy(() => import('@/page/display/DisplayPage'));
const StopTemplate = lazy(() => import('@/page/stopTemplate/StopTemplatePage'));
const StopPage = lazy(() => import('@/page/stop/StopPage'));
const StopMapPage = lazy(() => import('@/page/stopmap/StopMapPage'));
const ProductPage = lazy(() => import('@/page/product/ProductPage'));
const UserPage = lazy(() => import('@/page/user/UserPage'));
const Groups = lazy(() => import('@/page/group/GroupPage'));
const CompanyPage = lazy(() => import('@/page/company/CompanyPage'));
const StopTerminalStatus = lazy(
  () => import('@/page/stopTerminalStatus/StopTerminalStatus'),
);
const ProductLog = lazy(() => import('@/page/productLog/ProductLogPage'));
const ProductConsole = lazy(() => import('@/page/productConsole/ProductConsolePage'));
const TimeTableSource = lazy(() => import('@/page/timeTableSource/TimeTableSourcePage'));

const FirmwarePage = lazy(() => import('@/page/firmware/FirmwarePage'));
const FirmwareUpgradeTimePage = lazy(
  () => import('@/page/firmwareUpgradeTime/FirmwareUpgradeTimePage'),
);
const HardwareVersionPage = lazy(
  () => import('@/page/hardwareVersion/HardwareVersionPage'),
);
const ServerPage = lazy(() => import('@/page/server/ServerPage'));
const AnnouncementPage = lazy(() => import('@/page/announcement/AnnouncementPage'));
const AnnouncementEditorPage = lazy(
  () => import('@/page/announcement/AnnouncementEditorPage'),
);
const DictionaryPage = lazy(() => import('@/page/dictionary/DictionaryPage'));

const baseRoute = createRootRoute({
  component: () => <Outlet />,
});

const rootRoute = createRoute({
  getParentRoute: () => baseRoute,
  path: '/',
  component: RootLayout,
});

const signInRoute = createRoute({
  getParentRoute: () => baseRoute,
  path: APP_ROUTES.USER.SIGN_IN,
  component: SignInPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => baseRoute,
  path: APP_ROUTES.USER.FORGOT_PASSWORD,
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => baseRoute,
  path: APP_ROUTES.USER.NEW_PASSWORD_TAN_STACK_ROUTER,
  component: ResetPasswordPage,
});

const authRoutes = [signInRoute, forgotPasswordRoute, resetPasswordRoute];

interface ProtectedRouteOptions {
  validateSearch?: (search: Record<string, unknown>) => Record<string, unknown>;
}

const createProtectedRoute = (
  parent: typeof rootRoute,
  path: string,
  component: React.ReactNode,
  permissions: PermissionType[],
  routeOptions?: ProtectedRouteOptions,
) =>
  createRoute({
    getParentRoute: () => parent,
    path,
    component: () => (
      <RoleProtectedPage roles={permissions} fallback={<div></div>}>
        {component}
      </RoleProtectedPage>
    ),
    ...routeOptions,
  });

const appRoutes = [
  createRoute({
    getParentRoute: () => rootRoute,
    path: APP_ROUTES.DASHBOARD,
    component: DashboardPage,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/flag', component: FlagPage }),
  createProtectedRoute(
    rootRoute,
    APP_ROUTES.TEMPLATES.OPERATIONAL_SCHEMES,
    <StateManagementPage />,
    [PermissionType.PERM_MENU_STATE_VIEW],
  ),
  createProtectedRoute(rootRoute, '/displays', <DisplayPage />, [
    PermissionType.PERM_MENU_DISPLAY_VIEW,
  ]),
  createProtectedRoute(rootRoute, '/images', <ImagePage />, [
    PermissionType.PERM_MENU_IMAGE_VIEW,
  ]),
  createProtectedRoute(rootRoute, APP_ROUTES.TEMPLATES.LAYOUTS, <LayoutPage />, [
    PermissionType.PERM_MENU_LAYOUT_VIEW,
  ]),
  createProtectedRoute(
    rootRoute,
    APP_ROUTES.TEMPLATES.TIMETABLES,
    <DynamicTimetablesPage />,
    [PermissionType.PERM_MENU_DYNAMIC_TIMETABLE_VIEW],
  ),
  createProtectedRoute(
    rootRoute,
    APP_ROUTES.TEMPLATES.TIMETABLE_EDITOR,
    <DynamicTimeTableEditor />,
    [PermissionType.PERM_MENU_DYNAMIC_TIMETABLE_VIEW],
  ),
  createRoute({
    getParentRoute: () => rootRoute,
    path: APP_ROUTES.TEMPLATES.LAYOUT_EDITOR,
    component: LayoutEditorPage,
  }),
  createProtectedRoute(rootRoute, '/fonts', <FontPage />, [
    PermissionType.PERM_MENU_RASTER_FONT_VIEW,
  ]),
  createProtectedRoute(rootRoute, '/stop-template', <StopTemplate />, [
    PermissionType.PERM_MENU_STOP_TEMPLATE_VIEW,
  ]),
  createProtectedRoute(
    rootRoute,
    APP_ROUTES.REAL_VIEW.STOP_LIST,
    <StopPage />,
    [PermissionType.PERM_MENU_STOP_VIEW],
    { validateSearch: validateStopSearch },
  ),
  createProtectedRoute(rootRoute, '/stop-map', <StopMapPage />, [
    PermissionType.PERM_MENU_MAP_VIEW,
  ]),
  createProtectedRoute(rootRoute, '/product', <ProductPage />, [
    PermissionType.PERM_MENU_PRODUCT_SN_VIEW,
  ]),
  createProtectedRoute(rootRoute, '/users', <UserPage />, [
    PermissionType.PERM_MENU_USER_VIEW,
  ]),
  createProtectedRoute(rootRoute, '/groups', <Groups />, [
    PermissionType.PERM_MENU_GROUP_VIEW,
  ]),
  createProtectedRoute(rootRoute, '/companies', <CompanyPage />, [
    PermissionType.PERM_MENU_COMPANY_VIEW,
  ]),
  createProtectedRoute(rootRoute, '/stop-terminal-status', <StopTerminalStatus />, [
    PermissionType.PERM_MENU_TERMINAL_STATUSES_VIEW,
  ]),
  createProtectedRoute(rootRoute, '/product-log', <ProductLog />, [
    PermissionType.PERM_MENU_PRODUCT_LOGS_VIEW,
  ]),
  createProtectedRoute(rootRoute, '/product-console', <ProductConsole />, [
    PermissionType.PERM_MENU_PRODUCT_CONSOLE_VIEW,
  ]),
  createProtectedRoute(rootRoute, '/timetable-data', <TimeTableSource />, [
    PermissionType.PERM_MENU_TIMETABLE_SOURCE_DATA_VIEW,
  ]),
  createProtectedRoute(rootRoute, '/firmware', <FirmwarePage />, [
    PermissionType.PERM_MENU_FIRMWARE_VIEW,
  ]),
  createProtectedRoute(
    rootRoute,
    '/firmware-terminal-update-list',
    <FirmwareUpgradeTimePage />,
    [PermissionType.PERM_MENU_FIRMWARE_VIEW],
  ),
  createProtectedRoute(
    rootRoute,
    APP_ROUTES.FIRMWARE.HARDWARE_VERSIONS,
    <HardwareVersionPage />,
    [PermissionType.PERM_MENU_HARDWARE_VERSION_VIEW],
  ),
  createProtectedRoute(rootRoute, '/server-list', <ServerPage />, [
    PermissionType.PERM_MENU_SERVER_VIEW,
  ]),
  createProtectedRoute(
    rootRoute,
    '/announcements',
    <AnnouncementPage />,
    [PermissionType.PERM_MENU_ANNOUNCEMENT_VIEW],
    { validateSearch: validateAnnouncementSearch },
  ),
  createRoute({
    getParentRoute: () => rootRoute,
    path: APP_ROUTES.ANNOUNCEMENTS.EDITOR,
    component: AnnouncementEditorPage,
  }),

  createRoute({
    getParentRoute: () => rootRoute,
    path: APP_ROUTES.USER.PROFILE,
    component: ProfilePage,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: APP_ROUTES.ERROR,
    component: ErrorPage,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: APP_ROUTES.TEMPLATES.DICTIONARY,
    component: DictionaryPage,
  }),
];

const routeTree = baseRoute.addChildren([
  rootRoute.addChildren(appRoutes),
  ...authRoutes,
]);

// @ts-ignore
export const router = createRouter({ routeTree });
